import axios from 'axios';
import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { NotificationManager } from 'react-notifications';
import { ATTACHMENT_TYPES } from '../government_book/details_expedient/ExpedientDetails';
import { bodyModalAddEvidenceFile } from '../government_book/details_expedient/expedient_details_logic';
import { fieldsValidation } from '../government_book/my_expedients/expedients_list_logic';
import ModalComponent from './ModalComponent';

/**
 * @typedef {Object} AnnexUploadResult
 * @property {boolean} error Whether the upload failed.
 * @property {number} [status] Upload status: 2 uploaded, 3 failed.
 * @property {string} [message] Human readable outcome.
 * @property {Object} file The annex the result belongs to.
 */

/**
 * Notifies which annexes failed to upload and releases the parent's lock so the user can retry.
 *
 * @param {Array<AnnexUploadResult>} uploadResults Outcome of each annex upload.
 * @param {Object} [callbacks]
 * @param {?function(boolean): void} [callbacks.setLoadUploadAnex] Toggles the parent's annex upload flag.
 * @param {?function(Array<Object>): void} [callbacks.onUploadError] Receives the annexes that failed.
 * @returns {Array<Object>} The annexes that failed; empty when every upload succeeded.
 */
export const notifyFailedUploads = (
  uploadResults,
  { setLoadUploadAnex = null, onUploadError = null } = {}
) => {
  const failedFiles = uploadResults
    .filter((uploadResult) => uploadResult['error'])
    .map((uploadResult) => uploadResult['file']);
  if (failedFiles.length === 0) {
    return failedFiles;
  }

  setLoadUploadAnex && setLoadUploadAnex(false);
  const failedAliases = failedFiles.map((file) => `"${file['alias']}"`).join(', ');
  NotificationManager.error(
    `El documento se registró, pero falló la carga de ${
      failedFiles.length === 1 ? 'el anexo' : 'los anexos'
    } ${failedAliases}. Da clic en "Reintentar" para volver a intentarlo.`,
    'Error al cargar anexos',
    10000
  );
  onUploadError && onUploadError(failedFiles);
  return failedFiles;
};

/**
 * Runs an annex upload and turns a rejected request (network error or 4xx/5xx response)
 * into a failed result, so the annex is marked as retryable instead of hanging the upload.
 *
 * @param {function(Object): Promise<AnnexUploadResult>} uploadDocument Uploads a single annex.
 * @param {Object} file Annex to upload.
 * @returns {Promise<AnnexUploadResult>}
 */
export const safeUploadDocument = (uploadDocument, file) =>
  uploadDocument(file).catch((error) => {
    console.error(error);
    return { error: true, status: 3, message: 'La subida del archivo falló', file: file };
  });

/**
 * Uploads the given annexes in parallel and updates the status of each one: 2 uploaded, 3 failed.
 *
 * @param {Array<Object>} files Annexes to upload; their status is updated in place.
 * @param {function(Object): Promise<AnnexUploadResult>} uploadDocument Uploads a single annex.
 * @returns {Promise<Array<AnnexUploadResult>>} Outcome of each upload, in the same order.
 */
export const uploadAnnexes = (files, uploadDocument) =>
  Promise.all(
    files.map(async (file) => {
      const uploadResult = await safeUploadDocument(uploadDocument, file);
      file['status'] = uploadResult['error'] ? 3 : 2;
      return uploadResult;
    })
  );

/**
 * Table of annexes for a new document; uploads them once the document is saved.
 *
 * @param {Object} props
 * @param {Object} props.acceptFiles Accepted file types for the dropzone.
 * @param {function(Array<Object>): void} props.getFiles Receives the current list of annexes.
 * @param {string} props.documentType Type of the document the annexes belong to.
 * @param {number|string} props.expedientId Expedient the document belongs to.
 * @param {?Object} props.dataUploadEvidences Saved document data; a new value starts the upload.
 * @param {function(string, string, number): Promise<boolean>} props.reloadDataComponent Refreshes the expedient once every annex is uploaded.
 * @param {boolean} [props.isMultiSignature=false] Whether the document is multi-signed.
 * @param {?function(boolean): void} [props.setLoadUploadAnex=null] Toggles the parent's annex upload flag.
 * @param {?function(Array<Object>): void} [props.onUploadError=null] Called with the annexes that failed to upload.
 * @returns {JSX.Element}
 */
const TableUploadManyFiles = ({
  acceptFiles,
  getFiles,
  documentType,
  expedientId,
  dataUploadEvidences,
  reloadDataComponent,
  isMultiSignature = false,
  setLoadUploadAnex = null,
  onUploadError = null,
}) => {
  const [listOfFiles, setListOfFiles] = useState([]);
  const [fileState, setFileState] = useState({});
  const [errors, setErrors] = useState({});
  const [show, setShow] = useState(false);
  const { authentication_token_03 } = Cookies.get();
  const [filesTried, setFilesTried] = useState([]);
  // Once the document is saved its annex list is fixed: annexes can only be retried.
  const isDocumentSaved = Boolean(dataUploadEvidences);

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptFiles,
    onDrop: (acceptedFiles) => {
      acceptedFiles.forEach((file, index) => {
        if (file['size'] === 0) {
          NotificationManager.error(
            `El archivo "${file['name']}" está vacío (0 bytes) y no puede subirse`
          );
          setErrors({
            ...errors,
            evidence_file: ['El archivo seleccionado está vacío, elige otro archivo'],
          });
          return;
        }

        if (!file['type'].includes('image')) {
          Object.assign(file, {
            preview: process.env.PUBLIC_URL + '/img/document.svg',
          });
        } else {
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          });
        }
        setFileState({ ...fileState, evidence_file: file });
        setErrors({
          ...errors,
          evidence_file: [],
        });
      });
    },
  });

  /**
   * Removes an annex from the table before the document is saved.
   *
   * @param {Object} fileToDelete Annex to remove.
   * @returns {void}
   */
  const deleteFile = (fileToDelete) => {
    let newFiles = [...listOfFiles];
    const indexDelete = newFiles.map((file) => file['alias']).indexOf(fileToDelete['alias']);
    if (indexDelete !== -1) {
      newFiles.splice(indexDelete, 1);
      setListOfFiles(newFiles);
      getFiles(newFiles);
    }
  };

  /**
   * Validates the annex form and adds the annex to the table.
   *
   * @param {Object} fileToAdd Annex captured in the form.
   * @returns {void}
   */
  const addFileToTable = (fileToAdd) => {
    const requiredFields = ['evidence_type', 'alias', 'evidence_file'];
    const responseValidation = fieldsValidation(requiredFields, fileState);

    if (typeof responseValidation === 'object') {
      NotificationManager.error(
        'Existen errores que te impiden continuar, por favor revisa los campos marcados con rojo'
      );
      setErrors(responseValidation);
    } else {
      let newArr = [...listOfFiles];
      newArr.push(fileToAdd);
      setListOfFiles(newArr);
      setFileState({});
      setShow(false);
      getFiles(newArr);

      newArr.forEach((file) => {
        URL.revokeObjectURL(file['evidence_file']['preview']);
      });
    }
  };

  /**
   * Uploads every annex not uploaded yet and notifies the ones that failed.
   *
   * @returns {Promise<void>}
   */
  const executeFilesUploading = async () => {
    listOfFiles.forEach((file) => {
      file['status'] = file['status'] !== 2 ? 1 : 2;
    });
    setListOfFiles([...listOfFiles]);
    setLoadUploadAnex && setLoadUploadAnex(true);

    const uploadResults = await uploadAnnexes(listOfFiles, uploadDocument);

    setFilesTried([...listOfFiles]);
    notifyFailedUploads(uploadResults, { setLoadUploadAnex, onUploadError });
  };

  /**
   * Uploads a single annex to the endpoint of the document type; already uploaded annexes are skipped.
   *
   * @param {Object} file Annex to upload.
   * @returns {Promise<AnnexUploadResult>}
   */
  const uploadDocument = async (file) => {
    if (file['status'] !== 2) {
      let responseDocumentSave;
      let filedata = new FormData();
      filedata.append('annexed_type', file['evidence_type']);
      filedata.append('alias', file['alias']);
      filedata.append('evidence_file', file['evidence_file']);
      filedata.append('is_multi_signature', isMultiSignature);
      if (documentType === 'promotion') {
        filedata.append('promotion_id', dataUploadEvidences['promotion_id']);
        filedata.append('document_type', file['evidence_type']);
        responseDocumentSave = await axios({
          method: 'post',
          url: `${process.env.REACT_APP_URL_API_LARAVEL}/document_expedients/evidences/upload/${expedientId}`,
          data: filedata,
          headers: {
            Authorization: String(authentication_token_03),
          },
        });
      } else if (documentType === 'demand') {
        filedata.append('demand_id', dataUploadEvidences['demand_id']);
        filedata.append('document_type', file['evidence_type']);
        responseDocumentSave = await axios({
          method: 'post',
          url: `${process.env.REACT_APP_URL_API_LARAVEL}/document_expedients/evidences/demand/upload/${expedientId}`,
          data: filedata,
          headers: {
            Authorization: String(authentication_token_03),
          },
        });
      } else if (documentType === 'writ') {
        filedata.append('writ_id', dataUploadEvidences['writ_id']);
        filedata.append('document_type', file['evidence_type']);
        responseDocumentSave = await axios({
          method: 'post',
          url: `${process.env.REACT_APP_URL_API_LARAVEL}/document_expedients/evidences/writ/upload/${expedientId}`,
          data: filedata,
          headers: {
            Authorization: String(authentication_token_03),
          },
        });
      } else if (ATTACHMENT_TYPES.includes(documentType)) {
        filedata.append('document_type', dataUploadEvidences['document_type']);
        filedata.append('annexed_name', file['alias']);
        responseDocumentSave = await axios({
          method: 'post',
          url: `${process.env.REACT_APP_URL_API_LARAVEL}/document_expedients/evidences/other/upload/${expedientId}`,
          data: filedata,
          headers: {
            Authorization: String(authentication_token_03),
          },
        });
      }

      if (
        responseDocumentSave &&
        responseDocumentSave.data &&
        responseDocumentSave.data.data &&
        responseDocumentSave.data.data.evidenceDocument &&
        typeof responseDocumentSave.data.data.evidenceDocument === 'object'
      ) {
        return {
          error: false,
          status: 2,
          message: 'Archivo subido correctamente',
          file: file,
        };
      } else {
        return {
          error: true,
          status: 3,
          message: 'La subida del archivo falló',
          file: file,
        };
      }
    } else {
      return {
        error: false,
        status: 2,
        message: 'Archivo subido correctamente',
        file: file,
      };
    }
  };

  /**
   * Retries the upload of every annex that failed (status 3).
   *
   * @returns {Promise<void>}
   */
  const uploadAgain = async () => {
    const filesToRetry = listOfFiles.filter((fileUpload) => fileUpload['status'] === 3);
    filesToRetry.forEach((fileUpload) => {
      fileUpload['status'] = 1;
    });
    setListOfFiles([...listOfFiles]);
    setLoadUploadAnex && setLoadUploadAnex(true);

    const uploadResults = await uploadAnnexes(filesToRetry, uploadDocument);

    setFilesTried([...listOfFiles]);
    notifyFailedUploads(uploadResults, { setLoadUploadAnex, onUploadError });
  };

  useEffect(() => {
    (async function () {
      if (listOfFiles.length > 0) await executeFilesUploading();
    })();
  }, [dataUploadEvidences]);

  useEffect(() => {
    setTimeout(() => {
      setListOfFiles(filesTried);
    }, 1000);
  }, [filesTried]);

  useEffect(() => {
    (async function () {
      // The upload is only finished once there are annexes and every one was uploaded (status 2).
      const existsFilesErrors = listOfFiles.filter((file) => {
        return file['status'] !== 2;
      });

      if (
        listOfFiles.length > 0 &&
        existsFilesErrors.length === 0 &&
        typeof dataUploadEvidences === 'object' &&
        dataUploadEvidences
      ) {
        setLoadUploadAnex && setLoadUploadAnex(false);
        try {
          await reloadDataComponent(
            'La subida del documento y sus archivos de prueba ha sido exitosa',
            'Excelente',
            5000
          );
        } catch (error) {
          console.error(error);
          NotificationManager.error(
            'El documento y sus anexos se cargaron, pero no se pudo actualizar el expediente. Recarga la página para verlos.'
          );
        }
      }
    })();
  }, [listOfFiles]);

  return (
    <>
      <section className="container">
        <div style={{ float: 'right' }}>
          <Button
            onClick={() => setShow(true)}
            disabled={isDocumentSaved}
            style={{ fontWeight: 'bold', fontSize: '14px', backgroundColor: '#02cc98' }}
            size="md"
            variant="outline-light"
          >
            <strong>
              <i className="fas fa-plus" /> Agregar anexo
            </strong>
          </Button>
        </div>
        <div className="clearFloat" />
        <div className="mT-20" style={{ overflowX: 'scroll' }}>
          {listOfFiles.length > 0 ? (
            <table
              className="table table-collapsed"
              style={{
                border: '1px solid #a2a2a2',
                marginTop: '10px',
              }}
            >
              <tbody>
                <tr style={{ backgroundColor: '#02cc98' }}>
                  <th className="text-center button-see-evidence-documents">Documento:</th>
                  <th className="text-center button-see-evidence-documents">Tipo:</th>
                  <th className="text-center button-see-evidence-documents">Etiqueta:</th>
                  <th className="text-center button-see-evidence-documents">Acciones:</th>
                </tr>
                {listOfFiles.map((file, index) => (
                  <tr key={index}>
                    <td style={{ paddingTop: '15px' }} className="text-center">
                      {file['status'] === 1 ? (
                        <>
                          <img
                            width="40px"
                            src={process.env.PUBLIC_URL + '/img/loader.gif'}
                            alt="Cargando archivo"
                          />
                          <br />
                          <small>Subiendo archivo...</small>
                        </>
                      ) : file['status'] === 2 ? (
                        <>
                          <i className="fas fa-check-circle" style={{ color: 'green' }} />
                          <br />
                          <small>El archivo se subió correctamente</small>
                        </>
                      ) : file['status'] === 3 ? (
                        <>
                          <i className="fas fa-exclamation-triangle" style={{ color: 'red' }} />
                          <br />
                          <small style={{ fontSize: '8px' }}>La carga del archivo falló</small>
                          <br />
                          <Button
                            size="sm"
                            variant="info"
                            title="Reintentar"
                            style={{ fontSize: '10px' }}
                            onClick={uploadAgain}
                          >
                            <i
                              className="fas fa-cloud-upload-alt"
                              style={{ color: 'white', fontSize: '10px' }}
                            />{' '}
                            Reintentar
                          </Button>
                        </>
                      ) : (
                        index + 1
                      )}
                    </td>
                    <td style={{ paddingTop: '15px', maxWidth: '150px' }} className="text-center">
                      {file['evidence_type']}
                    </td>
                    <td style={{ paddingTop: '15px', maxWidth: '150px' }} className="text-justify">
                      {file['alias']}
                    </td>
                    <td style={{ paddingTop: '10px' }} className="text-center">
                      <Button
                        size="sm"
                        variant="danger"
                        title="Cancelar"
                        onClick={() => deleteFile(file)}
                        disabled={isDocumentSaved}
                      >
                        <i className="ti-trash" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            ''
          )}
        </div>
        <div className="clearFloat mT-10" />
        <small className="text-justify">
          * IMPORTANTE: Para agregar un anexo o prueba, haz clic en el botón{' '}
          <strong>Agregar anexo </strong>y proporciona la información solicitada por cada documento
          a subirse.
        </small>
      </section>

      <div className={show ? 'modal-superpositionated-fade' : 'd-n'} />
      <ModalComponent
        header={'Carga de anexo'}
        show={show}
        onHide={setShow}
        canClose={true}
        scroll={false}
        body={bodyModalAddEvidenceFile(
          setFileState,
          fileState,
          getRootProps,
          errors,
          setErrors,
          getInputProps
        )}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShow(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={() => addFileToTable(fileState)}>
              Agregar
            </Button>
          </>
        }
      />
    </>
  );
};

export default TableUploadManyFiles;
